"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { fetchListings, fetchUserProfile } from "@/lib/supabase/queries";
import { createClient } from "@/lib/supabase/client";
import type { StoredListing } from "@/lib/types";
import type { UserProfile } from "@/lib/supabase/queries";
import { formatRelativeDate } from "@/lib/format-date";
import MenuDrawer from "@/components/MenuDrawer";
import { ProfileCompletionBanner } from "@/components/ProfileCompletionBanner";
import { EmptyState } from "@/components/EmptyState";
import { CategoryBadge } from "@/components/CategoryBadge";
import { SkeletonCard } from "@/components/SkeletonCard";
import MarketingPage from "@/components/marketing/MarketingPage";

export default function HomePage() {
  const router = useRouter();
  const [listings, setListings] = useState<StoredListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Check auth first - show marketing page if not logged in
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      setIsAuthenticated(!!user);
      if (user) {
        const profile = await fetchUserProfile().catch(() => null);
        setProfile(profile);
        setProfileLoaded(true);
        setUserName(
          profile?.display_name ??
            user.user_metadata?.full_name ??
            user.user_metadata?.name ??
            user.email?.split("@")[0] ??
            null
        );
        setAvatarUrl(profile?.avatar_url ?? user.user_metadata?.avatar_url ?? null);
      }
    });
  }, []);

  useEffect(() => {
    // Only run onboarding/data checks for authenticated users
    if (isAuthenticated !== true) return;
    // Wait for profile to be fetched before checking onboarding
    if (!profileLoaded) return;
    if (localStorage.getItem("hasSeenOnboarding")) {
      setHasCheckedOnboarding(true);
      return;
    }
    // localStorage missing: check DB flag before redirecting
    if (profile?.onboarding_completed) {
      localStorage.setItem("hasSeenOnboarding", "true");
      setHasCheckedOnboarding(true);
    } else {
      router.push("/onboarding");
    }
  }, [router, isAuthenticated, profileLoaded, profile]);

  useEffect(() => {
    if (isAuthenticated !== true) return;
    setError(null);
    fetchListings()
      .then(setListings)
      .catch(() => setError("Impossible de charger vos créations. Vérifiez votre connexion."))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  // Re-fetch listings when returning to the page (fixes stale data after edit)
  useEffect(() => {
    if (isAuthenticated !== true) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchListings()
          .then(setListings)
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated]);

  const handleQuickCopy = async (e: React.MouseEvent, listing: StoredListing) => {
    e.preventDefault();
    e.stopPropagation();
    const text = `${listing.text.titre}\n\n${listing.text.description}\n\n${listing.text.hashtags.join(" ")}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API not available or permission denied — silent fail
      return;
    }
    setCopiedId(listing.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Auth loading state
  if (isAuthenticated === null) {
    return <div className="bg-background min-h-screen" />;
  }

  // Not authenticated - show marketing page
  if (!isAuthenticated) {
    return <MarketingPage />;
  }

  if (!hasCheckedOnboarding) {
    return <div className="bg-background min-h-screen" />;
  }

  return (
    <div className="bg-background text-on-surface min-h-screen pb-32">
      <MenuDrawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      {/* TopAppBar */}
      <header className="sticky top-0 z-50 flex w-full items-center justify-between bg-stone-50/80 px-4 py-4 shadow-sm shadow-stone-200/50 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMenuOpen(true)}
            className="hover:bg-surface-container-low flex h-9 w-9 items-center justify-center rounded-full transition-colors active:scale-95"
            aria-label="Ouvrir le menu"
          >
            <span className="material-symbols-outlined text-on-surface text-xl">menu</span>
          </button>
          <h1 className="font-headline text-on-surface text-xl italic">Vitrine</h1>
        </div>
        <Link
          href="/profile"
          className="bg-primary-fixed relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full transition-transform active:scale-95"
        >
          {avatarUrl ? (
            <Image
              alt="Photo de profil"
              className="object-cover"
              fill
              sizes="36px"
              src={avatarUrl}
            />
          ) : (
            <span
              className="material-symbols-outlined text-primary text-lg"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              person
            </span>
          )}
        </Link>
      </header>

      <main className="mx-auto max-w-2xl space-y-5 px-5 pt-5">
        {/* Welcome + Quick stats */}
        <section className="flex items-center justify-between">
          <div>
            <h2 className="text-on-surface font-headline text-2xl italic">
              Bonjour
              {userName ? (
                <>
                  , <span className="not-italic">{userName}</span>
                </>
              ) : null}
            </h2>
            {loading ? (
              <div className="bg-surface-container-high mt-1 h-3.5 w-36 animate-pulse rounded-full" />
            ) : listings.length > 0 ? (
              <p className="text-on-surface-variant mt-0.5 text-xs">
                {listings.length} création{listings.length > 1 ? "s" : ""}
              </p>
            ) : (
              <p className="text-on-surface-variant mt-0.5 text-xs italic">Prêt à commencer ?</p>
            )}
          </div>
        </section>

        {/* Quick Action — CTA */}
        <section>
          <Link
            href="/upload"
            className="group bg-primary flex items-center gap-4 rounded-2xl p-4 transition-all active:scale-[0.98]"
          >
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/20">
              <span
                className="material-symbols-outlined text-2xl text-white"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                add_a_photo
              </span>
            </div>
            <div className="flex-1 text-left">
              <span className="block text-sm font-semibold text-white">Nouvelle création</span>
              <span className="text-xs text-white/70">Photographiez votre vêtement</span>
            </div>
            <span className="material-symbols-outlined text-xl text-white/40 transition-transform group-hover:translate-x-1">
              arrow_forward
            </span>
          </Link>
        </section>

        {/* Profile completion nudge */}
        {profile && <ProfileCompletionBanner profile={profile} />}

        {/* Error state */}
        {error && (
          <section className="space-y-3 rounded-xl border border-red-200/60 bg-red-50 p-6">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined flex-shrink-0 text-xl text-red-500">
                error
              </span>
              <div className="space-y-1">
                <p className="font-body text-sm text-red-900">{error}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchListings()
                  .then(setListings)
                  .catch(() =>
                    setError("Impossible de charger vos créations. Vérifiez votre connexion.")
                  )
                  .finally(() => setLoading(false));
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-red-100 px-4 py-2 text-sm font-semibold text-red-800 transition-colors hover:bg-red-200 active:scale-95"
            >
              <span className="material-symbols-outlined text-base">refresh</span>
              Réessayer
            </button>
          </section>
        )}

        {/* Dernières Créations Grid */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-on-surface text-base font-semibold">Dernières créations</h3>
            {listings.length > 0 && (
              <Link
                className="btn-tertiary tracking-wider uppercase hover:opacity-70"
                href="/history"
              >
                Voir tout
              </Link>
            )}
          </div>

          {/* Loading skeletons */}
          {loading && (
            <div className="grid grid-cols-2 gap-4">
              <SkeletonCard variant="wide" />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {!loading && !error && listings.length > 0 ? (
            <div className="space-y-4">
              {listings.slice(0, 3).map((listing) => {
                const imageUrl =
                  listing.images.find((img) => img.variant === "studio_front")?.dataUrl ??
                  listing.images[0]?.dataUrl ??
                  listing.originalImageDataUrl;

                return (
                  <Link key={listing.id} href={`/results/${listing.id}`} className="group block">
                    <div className="bg-surface-container-highest relative aspect-[16/10] overflow-hidden rounded-2xl shadow-sm">
                      <Image
                        alt={listing.text.titre}
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        fill
                        sizes="(max-width: 768px) 100vw, 672px"
                        src={imageUrl}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      {listing.generationStatus === "generating_images" && (
                        <div className="absolute top-3 right-3 z-10">
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                            <span className="material-symbols-outlined animate-spin text-xs">
                              progress_activity
                            </span>
                            En cours
                          </span>
                        </div>
                      )}
                      {listing.text.categorie && (
                        <div className="absolute top-3 left-3">
                          <CategoryBadge className="bg-tertiary-container/90 font-label text-[11px] font-bold tracking-tighter text-white uppercase">
                            {listing.text.categorie}
                          </CategoryBadge>
                        </div>
                      )}
                      <button
                        onClick={(e) => handleQuickCopy(e, listing)}
                        aria-label="Copier pour Vinted"
                        className="absolute right-3 bottom-3 flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1.5 backdrop-blur-sm transition-transform active:scale-95"
                      >
                        <span className="material-symbols-outlined text-[16px] text-white">
                          {copiedId === listing.id ? "check" : "content_copy"}
                        </span>
                      </button>
                      <div className="absolute right-14 bottom-3 left-3 space-y-0.5">
                        <h4 className="text-base leading-tight font-semibold text-white drop-shadow-md">
                          {listing.text.titre}
                        </h4>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-white/70">
                            {formatRelativeDate(listing.date)}
                          </span>
                          <span className="text-sm font-bold text-white">
                            {listing.text.prix_suggere.max}&euro;
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : !loading && !error ? (
            <EmptyState
              icon="auto_awesome"
              headline="Chaque pi&#232;ce raconte une histoire"
              description="Photographiez votre premi&#232;re pi&#232;ce vintage et laissez Vitrine cr&#233;er une annonce professionnelle."
              action={{
                label: "Cr\u00e9er ma premi\u00e8re annonce",
                href: "/upload",
                icon: "add_a_photo",
              }}
              className="from-primary-fixed to-tertiary-fixed/50 rounded-xl bg-gradient-to-br"
            />
          ) : null}
        </section>
      </main>
    </div>
  );
}
